import { Check, ExternalLink, Globe, MapPin, Pencil, RefreshCw, Trash2, X, XCircle } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { Event, Person, updateEvent, updatePerson, selectEnrichmentOption, selectPersonEnrichmentOption, purgeEnrichmentSuggestion } from "../../interfaces/events";

interface EventDetailModalProps {
    event: Event;
    onClose: () => void;
    onTriggerEnrichment: (eventId: string, type: string) => void;
    onRefresh: () => void;
}

export default function EventDetailModal({ event, onClose, onTriggerEnrichment, onRefresh }: EventDetailModalProps) {
    const [editingEventEnrichment, setEditingEventEnrichment] = useState<number | null>(null);
    const [editingPersonEnrichment, setEditingPersonEnrichment] = useState<{personId: string, index: number} | null>(null);
    const [editData, setEditData] = useState<string>("");
    const [saving, setSaving] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [editingCompanyName, setEditingCompanyName] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    const [companyName, setCompanyName] = useState(event.companyName);
    const [description, setDescription] = useState(event.description || "");
    const [showingEventOptions, setShowingEventOptions] = useState<number | null>(null);
    const [showingPersonOptions, setShowingPersonOptions] = useState<{personId: string, index: number} | null>(null);

    const getStatusColor = (status: string) => {
        switch (status) {
            case "done": return "bg-green-50 text-green-800 border-green-200";
            case "pending": return "bg-yellow-50 text-yellow-800 border-yellow-200";
            case "error": return "bg-red-50 text-red-800 border-red-200";
            default: return "bg-gray-50 text-gray-800 border-gray-200";
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const handleEditEventEnrichment = (index: number, currentData: any) => {
        setEditingEventEnrichment(index);
        setEditData(currentData ? JSON.stringify(currentData, null, 2) : "");
    };

    const handleSaveEventEnrichment = async (index: number) => {
        setSaving(true);
        try {
            let parsedData;
            try {
                parsedData = editData.trim() ? JSON.parse(editData) : null;
            } catch {
                alert("Invalid JSON format");
                setSaving(false);
                return;
            }

            const updatedEnrichments = [...event.enrichments];
            updatedEnrichments[index] = {
                ...updatedEnrichments[index],
                data: parsedData,
                status: parsedData ? "done" : "pending" // Set to pending if no data
            };

            await updateEvent(event._id, { enrichments: updatedEnrichments });
            setEditingEventEnrichment(null);
            setEditData("");
            await onRefresh(); // Wait for refresh to complete
        } catch (err) {
            console.error("Failed to update enrichment:", err);
            alert("Failed to update enrichment");
        } finally {
            setSaving(false);
        }
    };

    const handleEditPersonEnrichment = (personId: string, index: number, currentData: any) => {
        setEditingPersonEnrichment({ personId, index });
        setEditData(currentData ? JSON.stringify(currentData, null, 2) : "");
    };

    const handleSavePersonEnrichment = async (personId: string, index: number) => {
        setSaving(true);
        try {
            let parsedData;
            try {
                parsedData = editData.trim() ? JSON.parse(editData) : null;
            } catch {
                alert("Invalid JSON format");
                setSaving(false);
                return;
            }

            const person = event.people.find(p => p._id === personId);
            if (!person) return;

            const updatedEnrichments = [...person.enrichments];
            updatedEnrichments[index] = {
                ...updatedEnrichments[index],
                data: parsedData,
                status: parsedData ? "done" : "pending" // Set to pending if no data
            };

            await updatePerson(event._id, personId, { enrichments: updatedEnrichments });
            setEditingPersonEnrichment(null);
            setEditData("");
            await onRefresh(); // Wait for refresh to complete
        } catch (err) {
            console.error("Failed to update person enrichment:", err);
            alert("Failed to update person enrichment");
        } finally {
            setSaving(false);
        }
    };

    const cancelEdit = () => {
        setEditingEventEnrichment(null);
        setEditingPersonEnrichment(null);
        setEditData("");
    };

    const handlePurgeEventEnrichment = async (enrichmentType: string) => {
        setSaving(true);
        try {
            await purgeEnrichmentSuggestion(event._id, { enrichmentType });
            await onRefresh();
        } catch (err) {
            console.error("Failed to purge enrichment:", err);
        } finally {
            setSaving(false);
        }
    };

    const handlePurgePersonEnrichment = async (personId: string, enrichmentType: string) => {
        setSaving(true);
        try {
            await purgeEnrichmentSuggestion(event._id, { enrichmentType, personId });
            await onRefresh();
        } catch (err) {
            console.error("Failed to purge person enrichment:", err);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveCompanyName = async () => {
        if (!companyName.trim()) {
            alert("Company name cannot be empty");
            return;
        }
        setSaving(true);
        try {
            await updateEvent(event._id, { companyName });
            setEditingCompanyName(false);
            await onRefresh();
        } catch (err) {
            console.error("Failed to update company name:", err);
            alert("Failed to update company name");
        } finally {
            setSaving(false);
        }
    };

    const handleSaveDescription = async () => {
        setSaving(true);
        try {
            await updateEvent(event._id, { description });
            setEditingDescription(false);
            await onRefresh();
        } catch (err) {
            console.error("Failed to update description:", err);
            alert("Failed to update description");
        } finally {
            setSaving(false);
        }
    };

    const cancelCompanyNameEdit = () => {
        setCompanyName(event.companyName);
        setEditingCompanyName(false);
    };

    const cancelDescriptionEdit = () => {
        setDescription(event.description || "");
        setEditingDescription(false);
    };

    const handleSelectEventOption = async (index: number, option: any) => {
        setSaving(true);
        try {
            const enrichment = event.enrichments[index];
            await selectEnrichmentOption(event._id, enrichment.type, option);
            setShowingEventOptions(null);
            await onRefresh();
        } catch (err) {
            console.error("Failed to select option:", err);
            alert("Failed to select option");
        } finally {
            setSaving(false);
        }
    };

    const handleSelectPersonOption = async (personId: string, index: number, option: any) => {
        setSaving(true);
        try {
            const person = event.people.find(p => p._id === personId);
            if (!person) return;
            
            const enrichment = person.enrichments[index];
            await selectPersonEnrichmentOption(event._id, personId, enrichment.type, option);
            setShowingPersonOptions(null);
            await onRefresh();
        } catch (err) {
            console.error("Failed to select option:", err);
            alert("Failed to select option");
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {/* Company Image */}
                        {event.enrichments?.find(e => e.type === "image" && e.data?.url) && (
                            <img 
                                src={event.enrichments.find(e => e.type === "image")?.data?.url} 
                                alt={event.companyName}
                                className="w-12 h-12 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setImagePreview(event.enrichments.find(e => e.type === "image")?.data?.url || null)}
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        )}
                        <div className="flex-1 group">
                            <h2 className="text-lg font-semibold text-navy-700">Event Details</h2>
                            {editingCompanyName ? (
                                <div className="flex items-center gap-2 mt-0.5">
                                    <input
                                        type="text"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        className="text-xs border border-gray-300 rounded px-2 py-1 flex-1"
                                        placeholder="Company name"
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleSaveCompanyName}
                                        disabled={saving}
                                        className="text-green-600 hover:text-green-800 disabled:text-gray-400"
                                    >
                                        <Check className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={cancelCompanyNameEdit}
                                        disabled={saving}
                                        className="text-gray-600 hover:text-gray-800 disabled:text-gray-400"
                                    >
                                        <XCircle className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <p className="text-xs text-gray-600">{event.companyName}</p>
                                    <button
                                        onClick={() => setEditingCompanyName(true)}
                                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 transition-opacity"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Description Section */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-navy-700">Description</h3>
                            {!editingDescription && (
                                <button
                                    onClick={() => setEditingDescription(true)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        {editingDescription ? (
                            <div className="flex flex-col gap-2">
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="text-sm bg-white border border-gray-300 p-3 rounded-xs min-h-[80px] w-full resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Add a description for this event..."
                                />
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSaveDescription}
                                        disabled={saving}
                                        className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-xs hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-1"
                                    >
                                        <Check className="w-3 h-3" />
                                        Save
                                    </button>
                                    <button
                                        onClick={cancelDescriptionEdit}
                                        disabled={saving}
                                        className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded-xs hover:bg-gray-700 disabled:bg-gray-400 flex items-center gap-1"
                                    >
                                        <XCircle className="w-3 h-3" />
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gray-50 border border-gray-200 rounded-xs p-3">
                                {description ? (
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{description}</p>
                                ) : (
                                    <p className="text-sm text-gray-400 italic">No description added yet. Click the edit icon to add one.</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Overview Section */}
                    <div className="mb-6">
                        <h3 className="text-sm font-semibold text-navy-700 mb-3">Overview</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
                                <p className="text-sm text-gray-900">{event.companyName}</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Event Type</label>
                                <p className="text-sm text-gray-900">{event.eventType || "N/A"}</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Source</label>
                                <p className="text-sm text-gray-900">{event.source}</p>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Source URL</label>
                                <a
                                    href={event.sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-gray-700 hover:underline truncate block"
                                >
                                    {event.sourceUrl}
                                </a>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Processing Stage</label>
                                <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded-sm border ${event.processing.stage === 'completed' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-gray-50 text-gray-800 border-gray-200'}`}>
                                    {event.processing.stage}
                                </span>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Detected At</label>
                                <p className="text-sm text-gray-900">{formatDate(event.detectedAt)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Enrichments Section */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-navy-700">Enrichments</h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => onTriggerEnrichment(event._id, "siren")}
                                    className="px-2 py-1 text-[11px] bg-black text-white rounded-sm hover:bg-gray-800 transition-colors"
                                >
                                    Trigger SIREN
                                </button>
                                <button
                                    onClick={() => onTriggerEnrichment(event._id, "website")}
                                    className="px-2 py-1 text-[11px] bg-black text-white rounded-sm hover:bg-gray-800 transition-colors"
                                >
                                    Trigger Website
                                </button>
                            </div>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xs overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Type</th>
                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Status</th>
                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Last Run</th>
                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Data</th>
                                        <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {event.enrichments.map((enr, i) => (
                                        <tr key={i} className="hover:bg-[#F9F6F3]">
                                            <td className="px-3 py-2 text-xs font-medium text-gray-900">{enr.type}</td>
                                            <td className="px-3 py-2">
                                                <span className={`px-2 py-0.5 text-[11px] font-medium rounded-sm border ${getStatusColor(enr.status)}`}>
                                                    {enr.status}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-gray-600">
                                                {enr.lastRunAt ? formatDate(enr.lastRunAt) : "Never"}
                                            </td>
                                            <td className="px-3 py-2">
                                                {editingEventEnrichment === i ? (
                                                    <div className="flex flex-col gap-2">
                                                        <textarea
                                                            value={editData}
                                                            onChange={(e) => setEditData(e.target.value)}
                                                            className="text-[10px] bg-white border border-gray-300 p-2 rounded font-mono min-h-[100px] w-full"
                                                            placeholder="Enter JSON data"
                                                        />
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleSaveEventEnrichment(i)}
                                                                disabled={saving}
                                                                className="px-2 py-1 text-[10px] bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                                                            >
                                                                <Check className="w-3 h-3 inline mr-1" />
                                                                Save
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                disabled={saving}
                                                                className="px-2 py-1 text-[10px] bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400"
                                                            >
                                                                <XCircle className="w-3 h-3 inline mr-1" />
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : showingEventOptions === i ? (
                                                    <div className="flex flex-col gap-2 max-w-md">
                                                        <div className="text-[10px] font-semibold text-gray-700 mb-1">Select an option:</div>
                                                        <div className="space-y-1 max-h-60 overflow-y-auto">
                                                            {enr.options && enr.options.length > 0 ? (
                                                                enr.options.map((opt, optIndex) => (
                                                                    <button
                                                                        key={optIndex}
                                                                        onClick={() => handleSelectEventOption(i, opt)}
                                                                        disabled={saving}
                                                                        className="w-full text-left px-2 py-1.5 text-[10px] bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded transition-colors disabled:bg-gray-100"
                                                                    >
                                                                        <pre className="whitespace-pre-wrap">{JSON.stringify(opt, null, 2)}</pre>
                                                                    </button>
                                                                ))
                                                            ) : (
                                                                <span className="text-xs text-gray-400">No options available</span>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => setShowingEventOptions(null)}
                                                            disabled={saving}
                                                            className="px-2 py-1 text-[10px] bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col gap-2">
                                                        {enr.data ? (
                                                            <pre className="text-[10px] bg-gray-100 p-2 rounded max-w-xs overflow-auto">
                                                                {JSON.stringify(enr.data, null, 2)}
                                                            </pre>
                                                        ) : (
                                                            <span className="text-xs text-gray-400">No data</span>
                                                        )}
                                                        {enr.options && enr.options.length > 0 && (
                                                            <button
                                                                onClick={() => setShowingEventOptions(i)}
                                                                className="px-2 py-1 text-[10px] bg-gray-100 text-white rounded hover:bg-gray-100 self-start"
                                                            >
                                                                View {enr.options.length} Option{enr.options.length > 1 ? 's' : ''}
                                                            </button>
                                                        )}
                                                        {enr.error && (
                                                            <p className="text-xs text-red-600 mt-1">{enr.error}</p>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    {(enr.type === "address" || enr.type === "restaurant_name") && (() => {
                                                        const address = event.enrichments?.find(e => e.type === "address")?.data?.address || "";
                                                        const name = event.enrichments?.find(e => e.type === "restaurant_name")?.data?.restaurant_name || "";
                                                        const query = `${name} ${address}`.trim();
                                                        if (!query) return null;
                                                        return (
                                                            <>
                                                                <a
                                                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-gray-400 hover:text-gray-700 transition-colors"
                                                                    title="Search on Google Maps"
                                                                >
                                                                    <MapPin className="w-4 h-4" />
                                                                </a>
                                                                <a
                                                                    href={`https://www.google.com/search?q=${encodeURIComponent(query)}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-gray-400 hover:text-gray-700 transition-colors"
                                                                    title="Search on Google"
                                                                >
                                                                    <Globe className="w-4 h-4" />
                                                                </a>
                                                            </>
                                                        );
                                                    })()}
                                                    {enr.type === "siren" && enr.data?.siren && (() => {
                                                        const siren = enr.data.siren;
                                                        const slugifiedName = event.companyName
                                                            .toLowerCase()
                                                            .replace(/[^a-z0-9]+/g, '-')
                                                            .replace(/^-+|-+$/g, '');
                                                        const url = `https://www.pappers.fr/entreprise/${slugifiedName}-${siren}`;
                                                        return (
                                                            <a
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-gray-400 hover:text-gray-700 transition-colors"
                                                                title="View on Pappers"
                                                            >
                                                                <ExternalLink className="w-4 h-4" />
                                                            </a>
                                                        );
                                                    })()}
                                                    {editingEventEnrichment !== i && (
                                                        <>
                                                            <button
                                                                onClick={() => handlePurgeEventEnrichment(enr.type)}
                                                                disabled={saving}
                                                                className="text-gray-300 hover:text-red-500 transition-colors disabled:opacity-50"
                                                                title="Purge enrichment"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEditEventEnrichment(i, enr.data)}
                                                                className="text-gray-900 hover:underline transition-colors"
                                                                title="Edit data"
                                                            >
                                                                <Pencil className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* People Section */}
                    <div>
                        <h3 className="text-sm font-semibold text-navy-700 mb-3">
                            People ({event.people?.length || 0})
                        </h3>
                        {event.people && event.people.length > 0 ? (
                            <div className="space-y-4">
                                {event.people.map((person: Person) => (
                                    <div key={person._id} className="bg-white border border-gray-200 rounded-xs overflow-hidden">
                                        {/* Person Header */}
                                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className="text-xs font-semibold text-navy-700">{person.name}</span>
                                                    <span className={`px-2 py-0.5 text-[11px] font-medium rounded-sm border ${person.role === "primary" ? "bg-gray-100 text-gray-700 border-gray-200" : "bg-gray-50 text-gray-800 border-gray-200"}`}>
                                                        {person.role}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-gray-600">Score: {person.relevanceScore}</span>
                                            </div>
                                        </div>
                                        
                                        {/* Person Enrichments */}
                                        {person.enrichments && person.enrichments.length > 0 && (
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Type</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Status</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Last Run</th>
                                                        <th className="px-3 py-2 text-left text-[11px] font-semibold text-gray-600 uppercase">Data</th>
                                                        <th className="px-3 py-2 text-right text-[11px] font-semibold text-gray-600 uppercase">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {person.enrichments.map((enr, i) => (
                                                        <tr key={i} className="hover:bg-[#F9F6F3]">
                                                            <td className="px-3 py-2 text-xs font-medium text-gray-900">{enr.type}</td>
                                                            <td className="px-3 py-2">
                                                                <span className={`px-2 py-0.5 text-[11px] font-medium rounded-sm border ${getStatusColor(enr.status)}`}>
                                                                    {enr.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-2 text-xs text-gray-600">
                                                                {enr.lastRunAt ? formatDate(enr.lastRunAt) : "Never"}
                                                            </td>
                                                            <td className="px-3 py-2">
                                                                {editingPersonEnrichment?.personId === person._id && editingPersonEnrichment?.index === i ? (
                                                                    <div className="flex flex-col gap-2">
                                                                        <textarea
                                                                            value={editData}
                                                                            onChange={(e) => setEditData(e.target.value)}
                                                                            className="text-[10px] bg-white border border-gray-300 p-2 rounded font-mono min-h-[100px] w-full"
                                                                            placeholder="Enter JSON data"
                                                                        />
                                                                        <div className="flex gap-2">
                                                                            <button
                                                                                onClick={() => handleSavePersonEnrichment(person._id, i)}
                                                                                disabled={saving}
                                                                                className="px-2 py-1 text-[10px] bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                                                                            >
                                                                                <Check className="w-3 h-3 inline mr-1" />
                                                                                Save
                                                                            </button>
                                                                            <button
                                                                                onClick={cancelEdit}
                                                                                disabled={saving}
                                                                                className="px-2 py-1 text-[10px] bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400"
                                                                            >
                                                                                <XCircle className="w-3 h-3 inline mr-1" />
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : showingPersonOptions?.personId === person._id && showingPersonOptions?.index === i ? (
                                                                    <div className="flex flex-col gap-2 max-w-md">
                                                                        <div className="text-[10px] font-semibold text-gray-700 mb-1">Select an option:</div>
                                                                        <div className="space-y-1 max-h-60 overflow-y-auto">
                                                                            {enr.options && enr.options.length > 0 ? (
                                                                                enr.options.map((opt, optIndex) => (
                                                                                    <button
                                                                                        key={optIndex}
                                                                                        onClick={() => handleSelectPersonOption(person._id, i, opt)}
                                                                                        disabled={saving}
                                                                                        className="w-full text-left px-2 py-1.5 text-[10px] bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded transition-colors disabled:bg-gray-100"
                                                                                    >
                                                                                        <pre className="whitespace-pre-wrap">{JSON.stringify(opt, null, 2)}</pre>
                                                                                    </button>
                                                                                ))
                                                                            ) : (
                                                                                <span className="text-xs text-gray-400">No options available</span>
                                                                            )}
                                                                        </div>
                                                                        <button
                                                                            onClick={() => setShowingPersonOptions(null)}
                                                                            disabled={saving}
                                                                            className="px-2 py-1 text-[10px] bg-gray-600 text-white rounded hover:bg-gray-700 disabled:bg-gray-400"
                                                                        >
                                                                            Cancel
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="flex flex-col gap-2">
                                                                        {enr.data ? (
                                                                            <pre className="text-[10px] bg-gray-100 p-2 rounded max-w-xs overflow-auto">
                                                                                {JSON.stringify(enr.data, null, 2)}
                                                                            </pre>
                                                                        ) : (
                                                                            <span className="text-xs text-gray-400">No data</span>
                                                                        )}
                                                                        {enr.options && enr.options.length > 0 && (
                                                                            <button
                                                                                onClick={() => setShowingPersonOptions({ personId: person._id, index: i })}
                                                                                className="px-2 py-1 text-[10px] bg-gray-100 text-white rounded hover:bg-gray-100 self-start"
                                                                            >
                                                                                View {enr.options.length} Option{enr.options.length > 1 ? 's' : ''}
                                                                            </button>
                                                                        )}
                                                                        {enr.error && (
                                                                            <p className="text-xs text-red-600 mt-1">{enr.error}</p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-2 text-right">
                                                                {!(editingPersonEnrichment?.personId === person._id && editingPersonEnrichment?.index === i) && (
                                                                    <div className="flex items-center justify-end gap-2">
                                                                        <button
                                                                            onClick={() => handlePurgePersonEnrichment(person._id, enr.type)}
                                                                            disabled={saving}
                                                                            className="text-gray-300 hover:text-red-500 transition-colors disabled:opacity-50"
                                                                            title="Purge enrichment"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleEditPersonEnrichment(person._id, i, enr.data)}
                                                                            className="text-gray-900 hover:underline transition-colors"
                                                                            title="Edit data"
                                                                        >
                                                                            <Pencil className="w-4 h-4" />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-gray-50 rounded-xs border border-gray-100">
                                <p className="text-xs text-gray-500">No people associated with this event</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <button
                        onClick={onRefresh}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-xs hover:bg-[#F9F6F3] transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-xs bg-black text-white rounded-xs hover:bg-gray-800 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Image Preview Modal */}
            {imagePreview && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4"
                    onClick={() => setImagePreview(null)}
                >
                    <div className="relative max-w-5xl max-h-[90vh]">
                        <button
                            onClick={() => setImagePreview(null)}
                            className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
                        >
                            <X className="w-8 h-8" />
                        </button>
                        <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-w-full max-h-[90vh] rounded-lg"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
}
